import { Link, MessageBar, MessageBarType, Stack, TextField } from "@fluentui/react"
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator"
import { useEffect, useState } from "react"
import QRCode from "react-qr-code"
import { getCurrentUserContactId, getCurrentUserName } from "../hooks/generalHooks"
import { makeCRMApiCall } from "../services/apiService"

interface MyCardProps {
	controlType: "WebResource" | "Portal"
}

export function MyCard({ controlType }: MyCardProps) {
	const [loading, setLoading] = useState<boolean>(true)
	const [contact, setContact] = useState<any>()
	const [error, setError] = useState<string>()
	const [noContact, setNoContact] = useState<boolean>(false)
	const currentUserContactId = getCurrentUserContactId()
	const currentUserEmail = getCurrentUserName()

	const fetchXml = `<fetch>
    <entity name="contact" >
      <attribute name="fullname" />
      <filter>
        <condition attribute="contactid" operator="eq" value="${currentUserContactId}" />
      </filter>
      <link-entity name="cw_householdmembers" from="cw_householdmembersid" to="cw_householdmember" link-type="inner" alias="member" >
        <attribute name="cw_grouprxcode" />
        <link-entity name="cw_employee" from="cw_employeeid" to="cw_employee" >
          <link-entity name="cw_universalplans" from="cw_universalplansid" to="cw_plan" alias="plan" >
            <attribute name="cw_name" />
            <attribute name="cw_plancode" />
          </link-entity>
          <link-entity name="account" from="accountid" to="cw_employer" alias="group" >
            <attribute name="name" />
            <attribute name="address1_city" />
            <attribute name="address1_line1" />
            <attribute name="address1_postalcode" />
            <attribute name="address1_stateorprovince" />
          </link-entity>
        </link-entity>
      </link-entity>
    </entity>
  </fetch>`

	useEffect(() => {
		const getContact = async (): Promise<any> => {
			setLoading(true)
			try {
				const escapedFetchXML = encodeURIComponent(fetchXml)
				const apiUrl = `contacts?fetchXml=${escapedFetchXML}`
				const contact = await makeCRMApiCall(apiUrl)
				if (contact.value.length === 0) {
					setNoContact(true)
					return
				}
				return contact.value[0]
			} catch (err) {
				console.log(err)
				setError("Error getting account")
			} finally {
				setLoading(false)
			}
		}

		const fetchData = async () => {
			try {
				const results = await getContact()
				setContact(results)
			} catch (err) {
				console.log(err)
				setError("Error getting account")
			} finally {
				setLoading(false)
			}
		}

		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const _getQRCodeUrl = (contact: any) => {
		const textToShow = `\b
        Name:/b ${contact.fullname}
        \r\n\b
        Group:/b ${contact["group.name"]}
        \r\n\b
        Street:/b ${contact["group.address1_line1"]}
        \r\n\b
        City:/b ${contact["group.address1_city"]}, ${contact["group.address1_stateorprovince"]}
        \r\n\b
        Group Plan:/b ${contact["member.cw_grouprxcode"]}`
		return `${window.location.origin}/card-info?data=${btoa(textToShow)}`
	}

	return (
		<>
			<Stack tokens={{ childrenGap: 10 }} style={{ padding: 15 }}>
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				{loading && <ProgressIndicator />}
				{noContact && (
					<MessageBar messageBarType={MessageBarType.warning}>
						Unable to find membership card information for: {currentUserEmail}. Please contact support for help by using the "Contact Us" link{" "}
						<Link target="_blank" href="https://www.acmg.md/">
							here
						</Link>
						.
					</MessageBar>
				)}
				{contact && (
					<Stack tokens={{ childrenGap: 10 }}>
						<TextField disabled label="Name" value={contact["group.name"]} />
						<TextField label="Address: Street 1" disabled value={contact["group.address1_line1"]} />
						<TextField disabled label="City" value={contact["group.address1_city"]} />
						<TextField disabled label="Group Plan #" value={contact["member.cw_grouprxcode"]} />
						<Stack tokens={{ childrenGap: 10 }}>
							{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
							<QRCode size={256} style={{ height: "auto", maxWidth: "600px", width: "100%" }} value={_getQRCodeUrl(contact)} viewBox="0 0 256 256" />
							<Link href={`${_getQRCodeUrl(contact)}`}>Click to see data</Link>
						</Stack>
					</Stack>
				)}
			</Stack>
		</>
	)
}
